// src/features/locations/LocationDetail.jsx
//
// First cut: Building + Lease panels only. Read-only. Other tabs (Cost,
// Utilization), inline editing, navigation chevrons, ID-collision guard,
// delete confirm, and the unsaved-changes guard are explicitly OUT of
// this slice and tracked in tasks/split_progress.md.
//
// Loads the location through locationsClient (cache hit if list was
// fetched first). Surfaces typed errors via ErrorBanner. Renders a
// Back button that calls onBack.

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { getLocation, updateStage, updateHealth, updateLocation, uploadLocationPhoto, deleteLocationPhoto, getCapacitySchedule, addCapacityScheduleRow, removeCapacityScheduleRow, editCapacityScheduleRow, getHeadcountSchedule, addHeadcountScheduleRow, removeHeadcountScheduleRow, editHeadcountScheduleRow } from "../../data/locationsClient.js";
import { flushSync } from "react-dom";
import { selectPrimaryLease } from "../leases/leaseSwitcherUtils.js";
import { leaseAreaSummary } from "../leases/leaseAreaSummary.js";
import { listLocationLeases } from "../../data/leasesClient.js";
import { getProjectDecisions } from "../../data/projectsClient.js";
import { AuthError, ConflictError, NetworkError, NotFoundError, ValidationError } from "../../data/errors.js";
import { buildPrefill, buildDiffPayload } from "./buildDiffPayload.js";
import { UnsavedChangesModal } from "./UnsavedChangesModal.jsx";
import { EditLocationModal } from "./EditLocationModal.jsx";
import { HEALTH, LAYERS, STAGES, T } from "../../app/tokens.js";
import { getMAndATagName } from "../../utils/mAndA.js";
import { S } from "../../app/styles.js";
import { Card } from "../../components/Card.jsx";
import { fmtDate, fmtMoney, fmtFootprintSqft as fmtSqft, getActiveLease } from "../../utils/format.js";
import { getDisplayedSqft } from "../../utils/locationSqftDerived.js";
import { StageBadge } from "../../components/StageBadge.jsx";
import { HealthBadge } from "../../components/HealthBadge.jsx";
import { HealthDot } from "../../components/HealthDot.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { showToast } from "../../components/toastService.jsx";
// O-36 — CostTab and FEATURE_LOCATION_COST_TAB retired; cost data now lives
// in the LeaseAndCostPane embedded in the Overview tab.
import { LeaseAndCostPane } from "./LeaseAndCostPane.jsx";
import { UtilizationTab } from "./UtilizationTab.jsx";
import { CandidatesPanel } from "./CandidatesPanel.jsx";
import { PhotoPanel } from "./PhotoPanel.jsx";
import { LocationDetailsPanel } from "./LocationDetailsPanel.jsx";
import { LocationOverviewCard } from "./LocationOverviewCard.jsx";
import { SpaceFitOverviewPane } from "./SpaceFitOverviewPane.jsx";
import { OfficeProjectsActivityPane } from "./OfficeProjectsActivityPane.jsx";
import {
  deriveCapacityScheduleSummary,
  deriveLifecyclePreviewRows,
} from "./capacityScheduleSummary.js";
// DecisionsActionsCard retired from LocationDetail (O-36); helpers are
// re-used internally by LeaseAndCostPane.

import { ExperienceTab } from "./ExperienceTab.jsx";
import { ServiceTab } from "./ServiceTab.jsx";
import { IntelTab } from "./IntelTab.jsx";
import { log } from "../../utils/logger.js";
import { rankSites } from "../../utils/rankSites.js";
import { loadPortfolioState, DEFAULT_PORTFOLIO_STATE } from "../../services/portfolioStatePersistence.js";
import { useTabState } from "../../v2/hooks/useTabState.js";
import { loadLastTab } from "../../services/tabStatePersistence.js";
import { applyAllFilters, applyAllFiltersAndSort, applySearchFilter } from "../../services/filterLocations.js";
import { applyColumnSort } from "../../utils/sortLocationsColumn.js";
import { useFeatureFlags } from "../../config/featureFlags.js";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { governedNavEnabled } from "../../app/governedNav.js";
import { DetailUnifiedBar } from "../../components/DetailUnifiedBar.jsx";
import { DetailNavBand } from "../../components/DetailRecordChrome.jsx";
import { RecordLayout, RecordBackHost, RecordActionsHost } from "../../components/OneRowChrome.jsx";
import { LocationNavPair } from "../../v2/locations/LocationNavPair.jsx";
import { buildLocationUrl, buildProjectsUrl } from "../../services/buildUrl.js";
import { ClosureOfferPanel, ClosureOfferChip } from "../workflows/ClosureOfferPanel.jsx";
import { ClosureStartDrawer } from "../workflows/ClosureStartDrawer.jsx";
import { getOpenClosureRunForLocation } from "../../data/workflowsClient.js";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { subscribeAttendanceSourceChange } from "../../services/cacheBus.js";
import { HeaderAreaLabel } from "../../components/HeaderAreaLabel.jsx";

// ─── Location-tab mapping ─────────────────────────────────────────────────────
//
// Maps feature_flags.key (surface='location_tab') → the internal tab key used
// by LocationDetail's activeTab state and the display label in the tab strip.
//
// Key invariants:
//   - "building" is the internal key for Overview (kept for initialTab compat).
//   - "people" is the internal key for Experience (kept for keep-alive state compat).
//   - Sort order comes from the DB (sort_order column); the order here is just docs.
const LOCATION_TAB_DB_KEY_MAP = {
  tab_overview:    { key: "building",    text: "Overview" },
  tab_utilization: { key: "utilization", text: "Utilization" },
  tab_experience:  { key: "people",      text: "Experience" },
  tab_service:     { key: "service",     text: "Service" },
  tab_intel:       { key: "intel",       text: "Intel" },
};


// LP1.3 Gate 2 — INLINE_REQUIRED is the subset of buildDiffPayload's
// REQUIRED_FIELDS that the inline LocationDetailsPanel actually exposes.
// currency + stage are required server-side on POST but PATCH allows
// partial updates, and neither is rendered as an inline input (currency
// is auto-inferred from country at create time; stage is edited via the
// identity-band StageBadge pill). Validating only the surfaced fields
// avoids blocking Save with errors the user has no inline way to fix.
// ADDR1: address is required — add it to the inline validation list so
// the user sees a per-field error when they try to save an empty address.
const INLINE_REQUIRED = ["market", "country", "region", "address"];

// LP1.3 Gate 2 — INLINE_SURFACED_FIELDS is the set of fields that
// LocationDetailsPanel actually renders as inline inputs (10 editable
// + ID stays read-only). It is the routing gate for per-field server
// ValidationErrors: a field-level error from PATCH only becomes an
// inline `editErrors[field]` entry if the user has a corresponding
// input to fix. Errors for unsurfaced editable fields (currency,
// district, floors, lat, lng — the 5 deferred to Phase-2 backlog —
// plus stage which is edited via the StageBadge pill) fall back to
// the form-level banner so the user is never silently blocked by a
// validation message they have no inline way to address.
//
// Architect-flagged during Gate 2 review (2026-05-12). Without this
// gate, EDITABLE_FIELDS would route currency/stage/etc. errors to a
// hidden state slot and auto-switch to the Location tab where there's
// nothing to display.
const INLINE_SURFACED_FIELDS = [
  "market", "locationAlias", "address", "suite", "country", "region",
  "installedCapacity", "maxCapacity", "mAndA", "onlineDate", "exitDate",
  "reopenDate", "plannedSeats",
];

// ─── Field row ─────────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...S.label, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: T.fsMd, color: T.dark }}>
        {children == null || children === "" ? <span style={{ color: T.light }}>—</span> : children}
      </div>
    </div>
  );
}

// ─── Building panel ───────────────────────────────────────────────────────
//
// Slice L4: unmounted; reserved for a future deep-detail view.
//
// As of L4 Gate 2 the tab dispatch no longer renders this panel — the
// "Location" tab now hosts the Photo + LocationDetailsPanel two-column
// layout (the always-visible header from L2/L3 was relocated into the
// tab content). The 14-field grid here is preserved as dead code so a
// future "deep detail" / "all fields" view can re-mount it without a
// resurrection diff. Do not delete the function; do not re-add it to
// the tab dispatch without an explicit slice scope.
function BuildingPanel({ location }) {
  const hlth = HEALTH[location.health] || { label: "Not Set" };
  return (
    <div style={S.panelPad}>
      <h3 style={S.sectionTitle}>Building</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "0 24px" }}>
        <Field label="Market">{location.market}</Field>
        <Field label="Location alias">{location.locationAlias}</Field>
        <Field label="Address">{location.address}</Field>
        <Field label="Suite">{location.suite}</Field>
        <Field label="Country">{location.country || "—"}</Field>{/* E3 chrome polish — was: {location.country}. Migration 0006 made country nullable; Field doesn't auto-fallback (Sqft row is the canonical pattern). */}
        <Field label="Region">{location.region}</Field>
        <Field label="M&A">{getMAndATagName(location.mAndA)}</Field>
        <Field label="Currency">{location.currency}</Field>
        <Field label="Sqft">{fmtSqft(getDisplayedSqft(location, null)) || "—"}</Field>
        <Field label="Installed capacity">{location.installedCapacity}</Field>
        <Field label="Max capacity">{location.maxCapacity}</Field>
        {/* FDOB-TO-ONLINEDATE-CONSOLIDATE-S1: FDOB retired; Online Date replaces it. */}
        <Field label="Online Date">{location.onlineDate ? fmtDate(location.onlineDate) : null}</Field>
        <Field label="Stage"><StageBadge stage={location.stage} /></Field>
        <Field label="Health">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <HealthDot health={location.health} size={12} />
            <span>{hlth.label}</span>
          </span>
        </Field>
      </div>
    </div>
  );
}

// LeasePanel is now imported from ./LeasePanel.jsx (slice 2b). The previous
// local read-only render lived here; it has been replaced by the
// interactive panel that supports add + edit through src/data/leasesClient.js.

// ─── Top-level ────────────────────────────────────────────────────────────

// Slice 11b: role flows in from the App shell via probeCurrentRole().
// The previous hardcoded CAN_WRITE / IS_ADMIN constants were safe in
// DEV_AUTH mode (server defaults to admin) but blocked any real gating.
// `currentRole` defaults to "admin" to preserve the existing behavior
// for callers that haven't been updated to pass it through yet.

// Slice L3 — `onNext` + `nextLabel` are optional. When both are
// provided, a right-anchored "Next: {market} →" affordance renders in
// the same band as the back button. When `onNext` is undefined (last
// ranked location, or list not yet loaded), the affordance is omitted
// entirely — chosen over a disabled-but-visible state because legacy
// hides it. Choice (1) from the brief: pass the next location's
// `market` as a separate `nextLabel` prop rather than the whole next
// location object — keeps LocationDetail's contract narrow and
// avoids leaking the full row schema into a navigation primitive.
export const LocationDetail = forwardRef(function LocationDetail({ locationId, onBack, onArchived, canArchive = false, initialTab = "building", backLabel = "Locations", currentRole = "admin", onNext, nextLabel, onPrev, prevLabel, showParentTrail = false, v2Shell = false,
  // LOCATION-HEADER-COLLAPSE-S1: slot rendered to the right of the pager in
  // the v2Shell unified bar. Contains Ask AI · bell · separator · user chip.
  // Null when not in v2Shell mode or not provided.
  headerRightSlot = null,
  headerSearch = null,
  searchOpen = false,
  // portfolioLocations + persisted filter state drive the
  // "Location X of Y in current view" caption and (future, L3)
  // Next/Previous nav. Keep them consumer-of-truth, not source.
  portfolioLocations = [],
  // LSE-LEASES-LIST-DETAIL-NAV-RESPECTS-LIST-SORT
  // navLeaseId: when the user navigated here from the Leases list, this is
  // the specific lease ID that was clicked. Passed down to LeaseSummaryTab
  // as overrideLeaseId so it can select the correct lease for multi-lease
  // locations and warn gracefully when the lease is not the primary lease.
  navLeaseId = undefined,
  // ASK-PORTFOLIO-S1: called with { type: 'location', id, label } to open the Ask popout.
  onAskOpen = undefined,
  // LOCATION-PAGER-RESPECT-SEARCH: active search term from the Portfolio view so the
  // "Location X of Y" caption reflects the same searched+filtered set as the pager.
  searchTerm = "",
  // LEASES-EDIT-INSTANT-UPDATE (location-detail path): called with the updated lease
  // object after a successful edit in LeaseSummaryTab so the parent (e.g. App) can
  // propagate the change to any sibling list views (LeasesPage) without waiting
  // for a full remount+refetch.
  onLeaseEditSaved = undefined,
  // OPEX-BUDGET-LINK-CTA: optional callback forwarded to CostTab so the "not linked"
  // empty state can offer a direct route to the budget-linking flow in Admin.
  onLinkBudget = undefined,
  // PLAN-SEATS-SYNC: called (with no arguments) after a successful inline edit
  // that changed `planned_seats`, so the parent (e.g. App) can invalidate its
  // cached planData and fetch fresh figures for the Plan page.
  onPlannedSeatsSaved = undefined,
  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: called (with no arguments) after a
  // successful Save that included pending capacity schedule ops, so the parent (e.g.
  // App) can bump portfolioRefreshKey and trigger an immediate OfficesPage re-fetch.
  onCapacityScheduleSaved = undefined,
  // OFFICES-HEADCOUNT-CONSUMERS: called (with no arguments) after a successful Save
  // that included pending headcount schedule ops, so the parent (e.g. App) can bump
  // portfolioRefreshKey and trigger an immediate OfficesPage re-fetch — making the
  // PEOPLE column, ASSIGN %, HEADROOM, and TOTAL HEADCOUNT KPI tile update without
  // a hard refresh.
  onHeadcountScheduleSaved = undefined,
  // W4 — CLOSURE-WF-REACTIVE-LAUNCH: display name of the current signed-in user;
  // used to pre-fill the owner field in ClosureStartDrawer when opened reactively.
  currentUserName = "",
  // W4 — CLOSURE-WF-REACTIVE-LAUNCH: optional callback to navigate to an existing
  // run when the dedup check finds one. When not provided, falls back to a direct
  // navigate("/workflows") style path.
  onOpenWorkflowRun = null,
  // DETAIL-PREVNEXT-PAGER: ordered ID list from the source list view, passed
  // via React Router navigation state. When present, renders PrevNextPager in
  // the unified bar instead of the legacy onPrev/onNext prop-driven buttons.
  orderedIds = null,
}, ref) {
  const navigate      = useNavigate();
  const routeState    = useRouterLocation().state;
  const CAN_WRITE = currentRole === "admin" || currentRole === "editor";
  const IS_ADMIN  = currentRole === "admin";
  const flags = useFeatureFlags();
  const { locationTabs, flags: governedFlags, flagsReady, nav_config, nav_order } = useFeatureFlagContext();
  const projectsNavEnabled = flagsReady && governedNavEnabled(governedFlags, nav_config, "projects", nav_order);
  const workflowsNavEnabled = flagsReady && governedNavEnabled(governedFlags, nav_config, "workflows", nav_order);
  const sensitivity = useSensitivityPolicy();
  const canSeeLeaseCostPane = sensitivity.canSee("offices.lease_cost_pane");
  // Task #8581: row-3 DOM slot that LeaseAndCostPane portals its "What's
  // ahead" card into, keeping one lease/opex fetch + derivation path.
  const [aheadSlotEl, setAheadSlotEl] = useState(null);
  const [location, setLocation] = useState(null);
  const [headerLeaseAreaDetails, setHeaderLeaseAreaDetails] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // O-36: Cost & Lease tab retired. "lease" and "cost" initialTab values previously
  // activated that tab; now they both redirect to Overview ("building").
  // AppShell actionNav=lease deep-links pass initTab:"lease" via state; landing
  // on Overview shows LeaseAndCostPane where the lease list is visible.
  const locationDefaultTab = "building";

  // OBJECT-TABS-PERSIST-LAST-SELECTION: Location Detail uses PATH-BASED routing
  // (/offices/:locationId/:tab). The initialTab prop carries the :tab path segment
  // (set by AppShell from locationTabMatch.params.tab). Normalise legacy "lease"
  // and "cost" → "building" (Overview) since the Cost & Lease tab is retired.
  // OFFICE-DEEPLINK-DEFAULT-OVERVIEW: when initialTab is "building" (i.e. the URL
  // has no :tab segment — AppShell.detailInitTab defaults to "building"), pass
  // "building" explicitly so useTabState and the localStorage-restore useEffect
  // both treat it as a real URL selection. This ensures a bare deep-link
  // (/offices/:id) always opens Overview, not the last-remembered tab.
  const urlTabFromPath = (initialTab === "lease" || initialTab === "cost")
    ? "building"
    : initialTab || "building";

  // Tab URL-sync callback: navigate to the canonical path-based URL
  // (/offices/:locationId/:tab) so tab state lives in the URL path segment,
  // not in a ?tab= query param. Strip the segment when tab is the default
  // ("building") to keep the canonical URL clean.
  // Uses replace so the Back button exits the page, not cycles through tabs.
  const handleLocationUrlChange = useCallback((key) => {
    const base = `/offices/${encodeURIComponent(locationId)}`;
    navigate(key === "building" ? base : `${base}/${key}`, {
      replace: true, state: routeState,
    });
  }, [navigate, locationId, routeState]);

  // Slice 7b: callers entering from the Lease Countdown report want the
  // Lease tab pre-selected. Default stays "building" for portfolio nav.
  // OBJECT-TABS-PERSIST-LAST-SELECTION: URL (path segment) > memory > defaultTab.
  const [activeTab, setActiveTab] = useTabState({
    groupKey:    "location",
    instanceId:  locationId,
    urlTab:      urlTabFromPath,
    validTabs:   ["building", "cost", "utilization", "people", "service", "intel"],
    defaultTab:  locationDefaultTab,
    onUrlChange: handleLocationUrlChange,
  });

  // OBJECT-TABS-PERSIST-LAST-SELECTION: when the URL has no tab path segment
  // (/offices/:id only), navigate to the remembered tab from localStorage.
  // Uses replace so Back still exits the page, not cycles through tabs.
  //
  // The guard ref is keyed by locationId (not a plain boolean) so that when
  // prev/next navigation reuses this mounted component for a different location,
  // the effect re-runs for the new location. This handles:
  //   • initial mount: locationId A, urlTabFromPath absent → restore memory
  //   • cross-location nav: locationId changes A→B (same component instance) →
  //     ref differs, effect re-runs and restores memory for B
  //   • tab clicks: locationId unchanged → ref matches, effect skips
  //
  // Reads directly from loadLastTab (not from activeTab state) to avoid a stale
  // closure: when deps fire after a locationId change, activeTab has not yet
  // updated to the new instance's value, but localStorage always has the right
  // value for the new locationId.
  const locationTabRestoredForRef = useRef(null);
  useEffect(() => {
    if (locationTabRestoredForRef.current === locationId) return;
    locationTabRestoredForRef.current = locationId;
    // If a non-default path segment is present, the URL is already correct.
    if (urlTabFromPath) return;
    // Read memory directly — avoids stale activeTab closure after cross-location nav.
    const remembered = loadLastTab("location", locationId);
    const validKeys = visibleTabs.map((t) => t.key);
    if (remembered && validKeys.includes(remembered) && remembered !== locationDefaultTab) {
      navigate(`/offices/${encodeURIComponent(locationId)}/${remembered}`, {
        replace: true, state: routeState,
      });
    }
  }, [locationId, urlTabFromPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // EXPERIENCE-TAB-FIX — amenity edit panel open state lifted here so the
  // "Edit amenities" button can live in the tab strip (same slot as
  // "Edit location" / "Edit lease") rather than inside the tab body.
  const [amenityEditOpen, setAmenityEditOpen] = useState(false);

  // LOCATION-TABS-CONTROL: derive the visible, ordered tab list from DB-backed
  // locationTabs context. Disabled tabs are excluded; Cost and Signals are also
  // gated by their compile-time / nav feature flags. Sort order is persisted
  // via the admin Location tabs panel and propagated through the context.
  const visibleTabs = useMemo(() => {
    return locationTabs
      .filter((t) => t.enabled)
      .filter((t) => {
        // O-36: Cost & Lease tab retired entirely — LeaseAndCostPane is now
        // embedded in the Overview tab. Always exclude, regardless of the DB flag.
        if (t.key === "tab_cost")                                    return false;
        return true;
      })
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((t) => {
        const def = LOCATION_TAB_DB_KEY_MAP[t.key];
        if (!def) return null;
        return { key: def.key, text: def.text, dbKey: t.key };
      })
      .filter(Boolean);
  }, [locationTabs]);

  // LOCATION-TABS-CONTROL: when a tab is hidden (by admin toggle or context update),
  // redirect to the first visible tab. Also guards the initialTab value if that tab
  // has been disabled. Runs only when visibleTabs or activeTab changes.
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const isVisible = visibleTabs.some((t) => t.key === activeTab);
    if (!isVisible) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  // Open decisions for the active project at this location.
  // Fetched when the location loads and an active project is present.
  // Failure is non-blocking — falls back to the derived lease renewal item.
  const [projectDecisions, setProjectDecisions] = useState([]);

  // CAPACITY-SCHEDULE-S1: schedule data for the edit modal's CapacityScheduleSection.
  //   null  = not yet loaded (fetch runs when editMode opens)
  //   object = { rows: [...], summary: {...} }
  const [capacitySchedule, setCapacitySchedule] = useState(null);
  const [capacityScheduleAddError, setCapacityScheduleAddError] = useState(null);
  const [capacityScheduleRemoving, setCapacityScheduleRemoving] = useState(new Set());
  // CAPACITY-SCHEDULE-S2: inline edit state.
  const [capacityScheduleEditing, setCapacityScheduleEditing] = useState(null);
  const [capacityScheduleEditError, setCapacityScheduleEditError] = useState(null);
  const [capacityScheduleRemoveError, setCapacityScheduleRemoveError] = useState(null);

  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: pending schedule operations buffer.
  // Ops are accumulated here instead of being written to the API immediately.
  // The modal renders a merged view; flush happens on Save; cleared on Cancel.
  // Op shapes:
  //   { op: 'add',    tempId: number,  payload: { effectiveDate, installedSeats, maxDensity, note } }
  //   { op: 'edit',   rowId:  number,  patch:   { effectiveDate, installedSeats, maxDensity, note } }
  //   { op: 'remove', rowId:  number }
  const [pendingScheduleOps, setPendingScheduleOps] = useState([]);
  // Monotonically-decreasing counter for synthetic IDs on pending-add rows.
  // Negative so they never clash with real DB integer IDs.
  const pendingTempIdRef = useRef(-1);

  // HEADCOUNT-SCHEDULE: headcount schedule data for the edit modal.
  //   null  = not yet loaded (fetch runs when editMode opens)
  //   object = { rows: [...] }
  const [headcountSchedule, setHeadcountSchedule] = useState(null);
  const [headcountScheduleAddError, setHeadcountScheduleAddError] = useState(null);
  const [headcountScheduleRemoving, setHeadcountScheduleRemoving] = useState(new Set());
  const [headcountScheduleEditing, setHeadcountScheduleEditing] = useState(null);
  const [headcountScheduleEditError, setHeadcountScheduleEditError] = useState(null);
  const [headcountScheduleRemoveError, setHeadcountScheduleRemoveError] = useState(null);

  // Pending headcount ops buffer — same pattern as pendingScheduleOps.
  // Op shapes:
  //   { op: 'add',    tempId: number,  payload: { effectiveDate, totalCount, note } }
  //   { op: 'edit',   rowId:  number,  patch:   { effectiveDate, totalCount, note } }
  //   { op: 'remove', rowId:  number }
  const [pendingHeadcountOps, setPendingHeadcountOps] = useState([]);
  const pendingHcTempIdRef = useRef(-1);

  // Reload bumps a counter that re-runs the effect. Used by LeasePanel's
  // onChanged so a successful write refetches the location (the leases
  // tree only lives on the parent location row). The locationCache was
  // already invalidated by leasesClient before this fires.
  const [reload, setReload] = useState(0);
  const refetch = useCallback(() => setReload((n) => n + 1), []);

  useEffect(() => subscribeAttendanceSourceChange(() => {
    setReload((n) => n + 1);
  }), []);

  // W4 — CLOSURE-WF-REACTIVE-LAUNCH
  // showClosureOffer: true after a successful stage change to "vacated".
  //   Cleared on dismiss (session-scoped: no server write).
  // closureOfferDismissed: true once the full panel is dismissed; shows
  //   the compact chip instead so the offer is never permanently lost.
  // closureDrawerOpen: true when ClosureStartDrawer is mounted.
  // closurePreFill: props forwarded to ClosureStartDrawer for pre-filling.
  const [showClosureOffer,    setShowClosureOffer]    = useState(false);
  const [closureOfferDismissed, setClosureOfferDismissed] = useState(false);
  const [closureDrawerOpen,   setClosureDrawerOpen]   = useState(false);
  const [closurePreFill,      setClosurePreFill]      = useState({
    sourceLocationId: "",
    closeDate:        "",
    ownerName:        "",
    launchedFrom:     "manual",
  });


  const archiveActiveRef = useRef(false);

  // ─── LP1.3 Gate 2: Inline Edit Location flow ──────────────────────────
  //
  // Replaces the L5 Gate 2 EditLocationModal with cross-tab inline edit
  // mode. Click "Edit location data" (tab-strip right-anchored button)
  // → editMode flips on, draft = buildPrefill(location), Save/Cancel
  // buttons replace the Edit button. The buttons live in the tab strip
  // so they persist across tab switches; the editable inputs only
  // render on the Location tab inside LocationDetailsPanel, but the
  // draft state lives here so switching tabs mid-edit does NOT discard
  // typed values. Save validates INLINE_REQUIRED (market/country/region
  // — see comment on the constant), buildDiffPayload() against the
  // stored row, sends only the diff via updateLocation(), refetches.
  // Cancel discards the draft + exits edit mode without writes.
  // Visibility gate: CAN_WRITE = admin || editor; server enforces 403.
  //
  // While editMode is true the StageBadge / HealthBadge pills are
  // rendered with canWrite={false} (display-only). Their click-to-edit
  // dropdowns write IMMEDIATELY and would refetch the row mid-edit —
  // nuking the draft. Disabling the pills during edit mode is the
  // smallest safe choice; the user can change Stage/Health after
  // Save/Cancel via the pills as before.
  const [editMode, setEditMode]               = useState(false);
  const [editDraft, setEditDraft]             = useState(null);
  const [editErrors, setEditErrors]           = useState({});
  const [editFormError, setEditFormError]     = useState("");
  const [editSubmitting, setEditSubmitting]   = useState(false);
  const [editNoChanges, setEditNoChanges]     = useState(false);
  const [editPhotoPending, setEditPhotoPending] = useState(null); // null | File | "DELETE"

  function handleEditOpen() {
    if (!location) return;
    setEditDraft(buildPrefill(location));
    setEditErrors({});
    setEditFormError("");
    setEditNoChanges(false);
    setPendingScheduleOps([]);
    setPendingHeadcountOps([]);
    setEditMode(true);
  }

  function handleEditPhotoChange(fileOrDelete) {
    setEditPhotoPending(fileOrDelete);
  }

  function handleEditCancel() {
    if (editSubmitting) return; // can't bail mid-flight
    setEditPhotoPending(null);
    setPendingScheduleOps([]);
    setPendingHeadcountOps([]);
    setEditMode(false);
    setEditDraft(null);
    setEditErrors({});
    setEditFormError("");
    setEditNoChanges(false);
  }

  function handleOfficeArchived(id) {
    // Commit the clean state before return navigation consults the exposed
    // guard. Draft fields, pending photos and schedules must never be saved.
    archiveActiveRef.current = false;
    flushSync(() => {
      handleEditCancel();
      setLocation(null);
      setPendingNav(null);
    });
    pendingNav?.resolve?.(false);
    onArchived?.(id);
    onBack?.();
  }

  // LSE-EDIT-LOCATION-MODAL-PHASE-1 — true when the current draft differs
  // from the stored location row. Passed to EditLocationModal to gate the
  // Discard confirm: a clean form closes immediately on Cancel/ESC.
  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: also true when there are
  // pending schedule ops so the Save button enables for schedule-only edits.
  const isEditDirty = useMemo(() => {
    if (pendingScheduleOps.length > 0) return true;
    if (pendingHeadcountOps.length > 0) return true;
    if (editPhotoPending !== null) return true;
    if (!editDraft || !location) return false;
    return Object.keys(buildDiffPayload(location, editDraft)).length > 0;
  }, [editDraft, editPhotoPending, location, pendingScheduleOps, pendingHeadcountOps]);

  function handleEditDraftChange(field, value) {
    setEditDraft((d) => (d ? { ...d, [field]: value } : d));
    if (editErrors[field]) setEditErrors((e) => ({ ...e, [field]: undefined }));
    if (editFormError) setEditFormError("");
    if (editNoChanges) setEditNoChanges(false);
  }

  // LP1.6 — Pure helper: derive the user-facing form-level message for
  // a typed PATCH error. Side-effect-free so both the inline-edit
  // form-error banner AND the LP1.6 unsaved-changes modal can render
  // the same message in their own chrome. Sister extraction to LP1.5's
  // `relativeTime` — pull the pure computation out, leave the
  // side-effects (logging, per-field routing, state writes) at the
  // call site. This keeps the error taxonomy in ONE place; if the
  // copy or the type list changes, both surfaces update together.
  function errorToFormMessage(err) {
    if (err instanceof ValidationError) return err.message || "Invalid request.";
    if (err instanceof ConflictError)   return err.message || "Conflict updating this location.";
    if (err instanceof NotFoundError)   return "Location not found. It may have been deleted by another admin.";
    if (err instanceof AuthError)       return "You don't have permission to edit locations.";
    return "Something went wrong. Please try again.";
  }

  // Mirrors EditLocationModal.applyErrorToUI verbatim — same error
  // taxonomy, same per-field vs form-level routing. ValidationError
  // with a field name in EDITABLE_FIELDS becomes an inline error;
  // anything else (including currency/stage validation errors that
  // can't surface inline) becomes a form-level banner so the user
  // is never silently blocked.
  // LP1.6 — message derivation now flows through `errorToFormMessage`
  // (above) so this side-effect path and the modal's render share one
  // taxonomy. Logging stays here because logs are themselves a side
  // effect we only want fired once per user-visible error event.
  function applyEditErrorToUI(err) {
    if (err instanceof ValidationError) {
      const field = err.details?.field;
      const msg = errorToFormMessage(err);
      // Route per-field errors only when the field has an inline
      // input the user can edit. Unsurfaced editable fields (currency,
      // stage, district, floors, lat, lng) fall back to the form-
      // level banner — see INLINE_SURFACED_FIELDS comment.
      if (field && INLINE_SURFACED_FIELDS.includes(field)) {
        setEditErrors({ [field]: msg });
        setActiveTab("building"); // ensure the inline error is visible
      } else {
        setEditFormError(msg);
      }
      return;
    }
    if (err instanceof ConflictError) {
      setEditFormError(errorToFormMessage(err));
      return;
    }
    if (err instanceof NotFoundError) {
      log.error("[LocationDetail edit] 404 from PATCH", { id: locationId, name: err.name, status: err.status });
      setEditFormError(errorToFormMessage(err));
      return;
    }
    if (err instanceof AuthError) {
      log.error("[LocationDetail edit] 403 from PATCH", { id: locationId, name: err.name, status: err.status });
      setEditFormError(errorToFormMessage(err));
      return;
    }
    log.error("[LocationDetail edit] update failed", {
      id: locationId,
      name: err?.name, message: err?.message,
      status: err?.status, isNetwork: err instanceof NetworkError,
    });
    setEditFormError(errorToFormMessage(err));
  }

  // ─── LP1.6: Unsaved-changes nav guard ──────────────────────────────────
  //
  // App.jsx wraps Next/Prev/back-to-Portfolio click handlers in
  // `await ref.current?.onBeforeNavigate(navAction, targetLabel)`. When
  // edit mode is OFF we resolve true synchronously and the nav fires
  // unimpeded. When edit mode is ON we open the three-action modal and
  // return a promise that resolves based on the user's choice:
  //   Cancel  → resolve(false), modal closes, nav blocked, edit mode preserved
  //   Discard → resolve(true),  draft cleared + edit mode exits, nav fires
  //   Save    → PATCH the diff; on success resolve(true) + nav fires
  //             (close-first ordering — same as inline Save), on error
  //             keep modal open with the message rendered INSIDE the
  //             modal (not the page-level form-error banner).
  //
  // Race guard: if pendingNav is already set when onBeforeNavigate is
  // called again (rapid double-click on a different nav button), we
  // return Promise.resolve(false) so the second click is a no-op
  // rather than silently losing the first promise's resolver.
  const [pendingNav, setPendingNav]           = useState(null);
  const [modalSaving, setModalSaving]         = useState(false);
  const [modalSaveError, setModalSaveError]   = useState("");

  useImperativeHandle(ref, () => ({
    onBeforeNavigate: (navAction, targetLabel) => {
      if (archiveActiveRef.current) return Promise.resolve(false);
      if (!editMode) return Promise.resolve(true);
      if (pendingNav) return Promise.resolve(false);
      return new Promise((resolve) => {
        setPendingNav({ navAction, targetLabel, resolve });
      });
    },
  }), [editMode, pendingNav]);

  function handleUnsavedCancel() {
    if (modalSaving) return; // can't bail mid-flight
    setModalSaveError("");
    const resolve = pendingNav?.resolve;
    setPendingNav(null);
    resolve?.(false);
  }

  function handleUnsavedDiscard() {
    if (modalSaving) return;
    // Mirror handleEditCancel's reset — clear every edit-mode state slot
    // so the next location's mount doesn't inherit stray draft/error.
    setEditPhotoPending(null);
    setPendingScheduleOps([]);
    setPendingHeadcountOps([]);
    setEditMode(false);
    setEditDraft(null);
    setEditErrors({});
    setEditFormError("");
    setEditNoChanges(false);
    setModalSaveError("");
    const resolve = pendingNav?.resolve;
    setPendingNav(null);
    resolve?.(true);
  }

  async function handleUnsavedSave() {
    if (modalSaving) return; // double-submit guard
    if (!location || !editDraft) return;

    // Same INLINE_REQUIRED gate as inline Save. If a required field
    // is blank we can't help the user inside the modal (the input
    // lives on the Location tab), so close the modal, surface the
    // inline errors on the page, and resolve(false) so the nav
    // doesn't fire — the user is now sitting on the same location
    // with the per-field error visible.
    const errs = {};
    for (const f of INLINE_REQUIRED) {
      if (!editDraft[f] || String(editDraft[f]).trim() === "") {
        errs[f] = "Required.";
      }
    }
    if (Object.keys(errs).length > 0) {
      setEditErrors(errs);
      setActiveTab("building");
      setModalSaveError("");
      const resolve = pendingNav?.resolve;
      setPendingNav(null);
      resolve?.(false);
      return;
    }

    const payload = buildDiffPayload(location, editDraft);
    setModalSaving(true);
    setModalSaveError("");
    try {
      if (Object.keys(payload).length > 0) {
        await updateLocation(location.id, payload);
      }
      // Close-first ordering — same as inline Save. Clear edit state
      // so the next location's mount renders cleanly, then resolve(true)
      // to let App.jsx fire the actual nav.
      setEditMode(false);
      setEditDraft(null);
      setEditErrors({});
      setEditFormError("");
      setEditNoChanges(false);
      setModalSaving(false);
      const resolve = pendingNav?.resolve;
      setPendingNav(null);
      resolve?.(true);
    } catch (err) {
      // Log once per user-visible error event (mirrors applyEditErrorToUI).
      if (err instanceof AuthError) {
        log.error("[LocationDetail unsaved-save] 403 from PATCH", { id: location.id, name: err.name, status: err.status });
      } else if (err instanceof NotFoundError) {
        log.error("[LocationDetail unsaved-save] 404 from PATCH", { id: location.id, name: err.name, status: err.status });
      } else if (!(err instanceof ConflictError) && !(err instanceof ValidationError)) {
        log.error("[LocationDetail unsaved-save] update failed", {
          id: location.id, name: err?.name, message: err?.message,
          status: err?.status, isNetwork: err instanceof NetworkError,
        });
      }
      setModalSaveError(errorToFormMessage(err));
      setModalSaving(false);
    }
  }

  async function handleEditSave() {
    if (editSubmitting) return; // double-submit guard
    if (!location || !editDraft) return;

    // Validate only the inline-surfaced required fields.
    const errs = {};
    for (const f of INLINE_REQUIRED) {
      if (!editDraft[f] || String(editDraft[f]).trim() === "") {
        errs[f] = "Required.";
      }
    }
    if (Object.keys(errs).length > 0) {
      setEditErrors(errs);
      setActiveTab("building"); // make the inline errors visible
      return;
    }

    const payload = buildDiffPayload(location, editDraft);
    const hasFieldChanges  = Object.keys(payload).length > 0;
    const hasPhotoChange   = editPhotoPending !== null;
    const hasScheduleOps   = pendingScheduleOps.length > 0;
    const hasHeadcountOps  = pendingHeadcountOps.length > 0;

    if (!hasFieldChanges && !hasPhotoChange && !hasScheduleOps && !hasHeadcountOps) {
      // No-op submit — surface inline notice rather than fire wasted request.
      setEditNoChanges(true);
      return;
    }

    setEditErrors({});
    setEditFormError("");
    setEditNoChanges(false);
    setEditSubmitting(true);
    try {
      // Photo change runs first. If this step fails the field PATCH is not
      // attempted — both changes succeed or neither does (atomic submission).
      if (editPhotoPending instanceof File) {
        await uploadLocationPhoto(location.id, editPhotoPending);
      } else if (editPhotoPending === "DELETE") {
        await deleteLocationPhoto(location.id);
      }
      if (hasFieldChanges) {
        await updateLocation(location.id, payload);
      }

      // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: flush pending schedule ops.
      // Compact first: skip edit ops for rows that are also being removed to
      // avoid a 404 on the server (remove wins over edit).
      if (hasScheduleOps) {
        const removedIds = new Set(
          pendingScheduleOps.filter((op) => op.op === "remove").map((op) => op.rowId),
        );
        const opsToFlush = pendingScheduleOps.filter(
          (op) => !(op.op === "edit" && removedIds.has(op.rowId)),
        );
        for (const op of opsToFlush) {
          if (op.op === "add") {
            await addCapacityScheduleRow(location.id, op.payload);
          } else if (op.op === "edit") {
            await editCapacityScheduleRow(location.id, op.rowId, op.patch);
          } else if (op.op === "remove") {
            await removeCapacityScheduleRow(location.id, op.rowId);
          }
        }
        setPendingScheduleOps([]);
        onCapacityScheduleSaved?.();
      }

      // HEADCOUNT-SCHEDULE: flush pending headcount ops (same compact pattern).
      if (hasHeadcountOps) {
        const removedHcIds = new Set(
          pendingHeadcountOps.filter((op) => op.op === "remove").map((op) => op.rowId),
        );
        const hcOpsToFlush = pendingHeadcountOps.filter(
          (op) => !(op.op === "edit" && removedHcIds.has(op.rowId)),
        );
        for (const op of hcOpsToFlush) {
          if (op.op === "add") {
            await addHeadcountScheduleRow(location.id, op.payload);
          } else if (op.op === "edit") {
            await editHeadcountScheduleRow(location.id, op.rowId, op.patch);
          } else if (op.op === "remove") {
            await removeHeadcountScheduleRow(location.id, op.rowId);
          }
        }
        setPendingHeadcountOps([]);
        // OFFICES-HEADCOUNT-CONSUMERS: notify the parent so OfficesPage
        // re-fetches and immediately reflects the new PEOPLE / ASSIGN% /
        // HEADROOM values without a hard refresh.
        onHeadcountScheduleSaved?.();
      }

      // Close-first ordering — match the L5 modal pattern.
      setEditMode(false);
      setEditDraft(null);
      setEditPhotoPending(null);
      setEditSubmitting(false);
      showToast("Changes saved.", { type: "success" });
      refetch();
      // PLAN-SEATS-SYNC: notify the parent so it can refetch planData and keep
      // the Plan page's expand table / drawer in sync without a full refresh.
      // Payload keys are camelCase (from buildDiffPayload). plannedSeats drives
      // Plan-page seat figures; installedCapacity drives utilisation % and
      // space-headroom KPIs.
      const PLAN_RELEVANT_FIELDS = ["plannedSeats", "installedCapacity"];
      if (hasFieldChanges && PLAN_RELEVANT_FIELDS.some((f) => f in payload)) {
        onPlannedSeatsSaved?.();
      }
    } catch (err) {
      applyEditErrorToUI(err);
      setEditSubmitting(false);
    }
  }

  // ─── LP1.2 Gate 1: inline Stage + Health editing ────────────────────────
  //
  // Wait-and-replace pattern (consistent with FxRatesPanel + L5 Edit
  // Modal): on success, refetch the location row. The badges await
  // these handlers and surface their own busy + inline-error state, so
  // no extra UI plumbing lives here. Errors that escape (rare — the
  // badges catch and display; this path only fires if the badge itself
  // is unmounted mid-flight) are surfaced via the existing ErrorBanner.
  // The badges' onChange are wrapped in the `if (CAN_WRITE)` ternary
  // upstream so viewers never receive an editable handler.
  async function handleStageChange(newStage) {
    try {
      await updateStage(locationId, newStage);
      showToast("Stage updated.", { type: "success" });
      refetch();
      // W4 — CLOSURE-WF-REACTIVE-LAUNCH: surface the offer panel after a
      // successful transition to Vacated. The transition always completes
      // unblocked; the offer is a follow-on only.
      if (newStage === "vacated") {
        const rawCloseDate = location?.closeDate ?? "";
        const normalizedCloseDate =
          rawCloseDate instanceof Date
            ? rawCloseDate.toISOString().slice(0, 10)
            : typeof rawCloseDate === "string" && rawCloseDate.includes("T")
              ? rawCloseDate.slice(0, 10)
              : String(rawCloseDate || "");
        setClosurePreFill({
          sourceLocationId: locationId,
          closeDate:        normalizedCloseDate,
          ownerName:        currentUserName || "",
          launchedFrom:     `${locationId} set to Vacated`,
        });
        setShowClosureOffer(true);
        setClosureOfferDismissed(false);
      }
    } catch (err) {
      log.error("LocationDetail: updateStage failed", { id: locationId, name: err?.name, msg: err?.message });
      setError(err);
      throw err; // re-throw so the badge keeps its menu open + inline error visible
    }
  }

  // W4 — CLOSURE-WF-REACTIVE-LAUNCH: dedup check + open drawer (or navigate
  // to existing run). Called from ClosureOfferPanel.onAccept and the chip.
  async function handleClosureOfferAccept() {
    if (!workflowsNavEnabled) return;
    try {
      const existing = await getOpenClosureRunForLocation(locationId);
      if (existing) {
        // An open run already exists — navigate to it rather than opening a
        // second-creation drawer. 409 is consumed silently; the user sees the run.
        if (onOpenWorkflowRun) {
          onOpenWorkflowRun(existing.id);
        } else {
          // Fallback: direct URL navigation (no router context available here).
          window.location.assign(`/workflows/runs/${existing.id}`);
        }
        return;
      }
    } catch (err) {
      // Non-fatal — log and fall through to opening the drawer.
      log.error("LocationDetail: dedup check failed, opening drawer anyway", { err: err?.message });
    }
    setClosureDrawerOpen(true);
  }

  async function handleHealthChange(newHealth) {
    try {
      await updateHealth(locationId, newHealth);
      showToast("Health updated.", { type: "success" });
      refetch();
    } catch (err) {
      log.error("LocationDetail: updateHealth failed", { id: locationId, name: err?.name, msg: err?.message });
      setError(err);
      throw err;
    }
  }

  // Keep the canonical primary lease available as read-only context for the
  // Lease & cost pane. Status editing belongs exclusively to LeaseDetailPage.
  const primaryLease = useMemo(
    () => selectPrimaryLease(location?.leases ?? []),
    [location?.leases]
  );
  useEffect(() => {
    let cancelled = false;
    setHeaderLeaseAreaDetails(null);

    if (!location || location.id !== locationId) return undefined;
    const linkedLeases = Array.isArray(location.leases) ? location.leases : [];
    const linkedLease = linkedLeases.length === 1 ? linkedLeases[0] : null;
    if (!linkedLease || Array.isArray(linkedLease.areas)) return undefined;

    listLocationLeases(locationId)
      .then((leases) => {
        if (cancelled) return;
        const detailedLease = (Array.isArray(leases) ? leases : [])
          .find((lease) => lease?.id === linkedLease.id);
        if (detailedLease && Array.isArray(detailedLease.areas)) {
          setHeaderLeaseAreaDetails({
            locationId,
            leaseId: linkedLease.id,
            areas: detailedLease.areas,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log.warn("LocationDetail: linked lease areas unavailable for header", {
            id: locationId,
            name: err?.name,
          });
        }
      });

    return () => { cancelled = true; };
  }, [locationId, location]);

  const headerAreaLabel = useMemo(() => {
    const linkedLeases = Array.isArray(location?.leases) ? location.leases : [];
    if (linkedLeases.length > 1) return "Multiple areas";
    if (linkedLeases.length === 1) {
      const linkedLease = linkedLeases[0];
      const areaDetails = headerLeaseAreaDetails?.locationId === locationId
        && headerLeaseAreaDetails.leaseId === linkedLease.id
        ? headerLeaseAreaDetails.areas
        : null;
      const leaseForSummary = areaDetails
        ? { ...linkedLease, areas: areaDetails }
        : linkedLease;
      return leaseAreaSummary(leaseForSummary, location?.suite);
    }
    return null;
  }, [location?.leases, location?.suite, headerLeaseAreaDetails, locationId]);

  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: Buffer an add op.
  // No API call — the op is accumulated and flushed when Save is clicked.
  // The modal renders from mergedCapacitySchedule (fetched + pending applied).
  function handleCapacityScheduleAdd(effectiveDate, installedSeats, maxDensity, note) {
    const tempId = (pendingTempIdRef.current -= 1);
    setPendingScheduleOps((ops) => [
      ...ops,
      { op: "add", tempId, payload: { effectiveDate, installedSeats, maxDensity, note } },
    ]);
  }

  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: Buffer a remove op.
  // For pending-add rows (tempId < 0) just drop the add op itself.
  // For real DB rows, append a remove op so they disappear in the merged view.
  function handleCapacityScheduleRemove(rowId) {
    if (rowId < 0) {
      setPendingScheduleOps((ops) =>
        ops.filter((op) => !(op.op === "add" && op.tempId === rowId)),
      );
    } else {
      setPendingScheduleOps((ops) => [...ops, { op: "remove", rowId }]);
    }
  }

  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: Buffer an edit op.
  // For pending-add rows (tempId < 0) update the existing add op's payload.
  // For real DB rows, upsert into an existing edit op or add a new one.
  function handleCapacityScheduleEdit(rowId, patch) {
    if (rowId < 0) {
      setPendingScheduleOps((ops) =>
        ops.map((op) =>
          op.op === "add" && op.tempId === rowId
            ? { ...op, payload: { ...op.payload, ...patch } }
            : op,
        ),
      );
    } else {
      setPendingScheduleOps((ops) => {
        const idx = ops.findIndex((op) => op.op === "edit" && op.rowId === rowId);
        if (idx >= 0) {
          const updated = [...ops];
          updated[idx] = { ...updated[idx], patch: { ...updated[idx].patch, ...patch } };
          return updated;
        }
        return [...ops, { op: "edit", rowId, patch }];
      });
    }
    setCapacityScheduleEditing(null);
  }

  // CAPACITY-SCHEDULE-SAVE-AND-REFRESH-FIX-S1: compute the merged schedule view.
  // Applies pendingScheduleOps on top of the fetched capacitySchedule rows so the
  // modal renders the user's in-progress edits without any API round-trips.
  // Re-sorts by effectiveDate DESC (newest-first) after each op application to match
  // the server GET response order.
  const mergedCapacitySchedule = useMemo(() => {
    if (!capacitySchedule) return null;
    let rows = [...(capacitySchedule.rows ?? [])];
    for (const op of pendingScheduleOps) {
      if (op.op === "add") {
        rows = [
          ...rows,
          {
            id: op.tempId,
            effectiveDate:  op.payload.effectiveDate,
            installedSeats: op.payload.installedSeats,
            maxDensity:     op.payload.maxDensity,
            note:           op.payload.note,
          },
        ];
      } else if (op.op === "remove") {
        rows = rows.filter((r) => r.id !== op.rowId);
      } else if (op.op === "edit") {
        rows = rows.map((r) => (r.id === op.rowId ? { ...r, ...op.patch } : r));
      }
    }
    rows = deriveLifecyclePreviewRows(rows, editDraft?.exitDate, editDraft?.reopenDate);
    rows = [...rows].sort((a, b) => {
      if (!a.effectiveDate) return 1;
      if (!b.effectiveDate) return -1;
      return b.effectiveDate.localeCompare(a.effectiveDate);
    });
    return {
      ...capacitySchedule,
      rows,
      summary: deriveCapacityScheduleSummary(
        rows,
        new Date(),
        editDraft?.exitDate,
        editDraft?.onlineDate,
        editDraft?.reopenDate,
      ),
    };
  }, [
    capacitySchedule,
    pendingScheduleOps,
    editDraft?.exitDate,
    editDraft?.reopenDate,
    editDraft?.onlineDate,
  ]);

  // ─── Headcount schedule handlers (mirror capacity schedule pattern) ──────

  function handleHeadcountScheduleAdd(effectiveDate, totalCount, note, split = {}) {
    const tempId = (pendingHcTempIdRef.current -= 1);
    setPendingHeadcountOps((ops) => [
      ...ops,
      { op: "add", tempId, payload: { effectiveDate, totalCount, note, ...split } },
    ]);
  }

  function handleHeadcountScheduleRemove(rowId) {
    if (rowId < 0) {
      setPendingHeadcountOps((ops) =>
        ops.filter((op) => !(op.op === "add" && op.tempId === rowId)),
      );
    } else {
      setPendingHeadcountOps((ops) => [...ops, { op: "remove", rowId }]);
    }
  }

  function handleHeadcountScheduleEdit(rowId, patch) {
    if (rowId < 0) {
      setPendingHeadcountOps((ops) =>
        ops.map((op) =>
          op.op === "add" && op.tempId === rowId
            ? { ...op, payload: { ...op.payload, ...patch } }
            : op,
        ),
      );
    } else {
      setPendingHeadcountOps((ops) => {
        const idx = ops.findIndex((op) => op.op === "edit" && op.rowId === rowId);
        if (idx >= 0) {
          const updated = [...ops];
          updated[idx] = { ...updated[idx], patch: { ...updated[idx].patch, ...patch } };
          return updated;
        }
        return [...ops, { op: "edit", rowId, patch }];
      });
    }
    setHeadcountScheduleEditing(null);
  }

  const mergedHeadcountSchedule = useMemo(() => {
    if (!headcountSchedule) return null;
    if (pendingHeadcountOps.length === 0) return headcountSchedule;
    let rows = [...(headcountSchedule.rows ?? [])];
    for (const op of pendingHeadcountOps) {
      if (op.op === "add") {
        rows = [
          ...rows,
          {
            id:            op.tempId,
            effectiveDate: op.payload.effectiveDate,
            totalCount:    op.payload.totalCount,
            fullTimeCount: op.payload.fullTimeCount ?? null,
            vendorContractorCount: op.payload.vendorContractorCount ?? null,
            independentContractorCount: op.payload.independentContractorCount ?? null,
            source:        "manual",
            note:          op.payload.note,
          },
        ];
      } else if (op.op === "remove") {
        rows = rows.filter((r) => r.id !== op.rowId);
      } else if (op.op === "edit") {
        rows = rows.map((r) => (r.id === op.rowId ? { ...r, ...op.patch } : r));
      }
    }
    rows = [...rows].sort((a, b) => {
      if (!a.effectiveDate) return 1;
      if (!b.effectiveDate) return -1;
      return b.effectiveDate.localeCompare(a.effectiveDate);
    });
    return { ...headcountSchedule, rows };
  }, [headcountSchedule, pendingHeadcountOps]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLocation(locationId)
      .then((loc) => {
        if (cancelled) return;
        setLocation(loc);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("LocationDetail: getLocation failed", { id: locationId, name: err?.name, msg: err?.message });
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [locationId, reload]);

  // CAPACITY-SCHEDULE-S1: Fetch the capacity schedule whenever edit mode opens
  // or the location changes while edit mode is active.  Non-blocking — a failure
  // leaves capacitySchedule as null so the section gracefully shows nothing.
  useEffect(() => {
    if (!editMode || !locationId) return undefined;
    let cancelled = false;
    setCapacitySchedule(null); // reset to loading state on each open
    getCapacitySchedule(locationId)
      .then((data) => { if (!cancelled) setCapacitySchedule(data); })
      .catch((err) => {
        if (!cancelled) {
          log.warn("LocationDetail: getCapacitySchedule failed (non-blocking)", { id: locationId, name: err?.name });
        }
      });
    return () => { cancelled = true; };
  }, [editMode, locationId]);

  // HEADCOUNT-SCHEDULE: Fetch the headcount schedule whenever edit mode opens.
  // Non-blocking — failure leaves headcountSchedule as null so the section
  // gracefully shows nothing (same pattern as capacity schedule above).
  useEffect(() => {
    if (!editMode || !locationId) return undefined;
    let cancelled = false;
    setHeadcountSchedule(null);
    setPendingHeadcountOps([]);
    getHeadcountSchedule(locationId)
      .then((data) => { if (!cancelled) setHeadcountSchedule(data); })
      .catch((err) => {
        if (!cancelled) {
          log.warn("LocationDetail: getHeadcountSchedule failed (non-blocking)", { id: locationId, name: err?.name });
        }
      });
    return () => { cancelled = true; };
  }, [editMode, locationId]);

  // Fetch open decisions for the active project at this location.
  // Runs whenever the location object changes (which also covers initial load).
  // Non-blocking: failure resets to [] so the card falls back to the derived
  // lease renewal reminder.
  useEffect(() => {
    if (!location || !projectsNavEnabled) {
      setProjectDecisions([]);
      return undefined;
    }
    const activeProject = (location.projects ?? []).find((p) => {
      const s = p.statusOverall ?? p.status_overall ?? p.status ?? "";
      return s === "active" || s === "in_progress" || s === "open";
    });
    if (!activeProject?.id) {
      setProjectDecisions([]);
      return undefined;
    }
    let cancelled = false;
    getProjectDecisions(activeProject.id)
      .then((list) => {
        if (!cancelled) setProjectDecisions(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (!cancelled) {
          log.warn("LocationDetail: getProjectDecisions failed (non-blocking)", {
            id: activeProject.id, name: err?.name,
          });
          setProjectDecisions([]);
        }
      });
    return () => { cancelled = true; };
  }, [location, projectsNavEnabled]);

  // P6 — Filter-aware positional caption.
  // Reads persisted Portfolio filter state (sessionStorage via service, never
  // direct storage access here). Applies the same ranked → filtered → sorted
  // pipeline as PortfolioView so the index is consistent with what the user
  // sees in the list/tiles/map view.
  const locationContextCaption = useMemo(() => {
    if (portfolioLocations.length === 0) return null;
    const persisted   = loadPortfolioState();
    const filters     = persisted?.filters     ?? DEFAULT_PORTFOLIO_STATE.filters;
    const sortKey     = persisted?.sortKey     ?? DEFAULT_PORTFOLIO_STATE.sortKey;
    const sortMode    = persisted?.sortMode    ?? DEFAULT_PORTFOLIO_STATE.sortMode;
    const listSortKey = persisted?.listSortKey ?? DEFAULT_PORTFOLIO_STATE.listSortKey;
    const listSortDir = persisted?.listSortDir ?? DEFAULT_PORTFOLIO_STATE.listSortDir;
    const ranked      = rankSites(portfolioLocations);
    // Mirror the same filter+sort pipeline as deriveNavLocations so the
    // "Location X of Y" caption stays consistent with the nav arrows.
    const baseSet = (sortMode === "column" && listSortKey)
      ? applyColumnSort(applyAllFilters(ranked, filters), listSortKey, listSortDir)
      : applyAllFiltersAndSort(ranked, filters, sortKey);
    // LOCATION-PAGER-RESPECT-SEARCH: apply the same search predicate as the
    // pager so "Location X of Y" count stays in sync with the nav arrows.
    const filteredSet = applySearchFilter(baseSet, searchTerm);
    const currentIndex = filteredSet.findIndex((loc) => loc.id === locationId);
    const filtersActive = (
      filters.health    !== "all"  ||
      filters.stage     !== "all"  ||
      filters.region    !== "all"  ||
      filters.mAndA     !== "all"  ||
      filters.leaseType !== "all"  ||
      filters.showClosed !== false
    );
    if (filtersActive) {
      return currentIndex >= 0
        ? `Location ${currentIndex + 1} of ${filteredSet.length} in current view`
        : "Not in current filtered view";
    }
    return currentIndex >= 0
      ? `Location ${currentIndex + 1} of ${filteredSet.length}`
      : null;
  }, [portfolioLocations, locationId, searchTerm]);

  return (
    <RecordLayout
      data-testid="location-detail-page"
      className={v2Shell ? "object-detail-layout" : undefined}
    >
      {/* LP1.2 Gate 2 — Three-zone background layering. Mirror of the
          legacy monolith (Ripple Workplace App.html lines 5856-6045):
            Zone 1 (title band) — full-bleed T.card, bottom T.border
            Zone 2 (tab strip)  — full-bleed T.card, top + bottom T.tintBorder
            Zone 3 (body)       — page T.bg shows through (no zone bg)
          Each zone is a full-width background <div> containing a
          centered maxWidth-1200 inner column. The single-maxWidth
          container that previously wrapped everything is split into
          these three siblings; the identity bar's own borderBottom
          and the tab strip's own borderBottom + marginBottom are
          removed so the new zone dividers don't duplicate them.
          AppHeaderBar's bottom edge stays visually distinct from
          Divider 1 because the title-band content sits between them.
          All four colors are existing T tokens — no new tokens
          introduced. */}

      {/* ── Zone 1: Title band (white) ───────────────────────────── */}
      <div
        data-testid="location-zone-title"
        className={v2Shell ? `v2-detail-header object-title-row object-title-two-line${searchOpen ? " is-search-open" : ""}` : undefined}
        style={{
          background: T.card,
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
          position: "relative",
          zIndex: searchOpen ? LAYERS.modal : LAYERS.stickyChrome,
        }}
      >
      {v2Shell && <RecordBackHost backLabel={backLabel} sectionLabel="Places" onBack={onBack} testId="location-back-button" />}
      {v2Shell ? (
        /* ── LOCATION-HEADER-S1: two-line identity header for v2Shell ──
           Line 1: market · alias · M&A  (muted dot separators, secondary text)
           Line 2: location ID · ◆ Hub chip (Hub only when is_hub=true)
           Stage pill removed per spec.
           LOCATION-HEADER-FIX-S1: pager moved to unified bar; right side of
           this header is now empty. ErrorBanner guarded so the aria-live
           slot only mounts when there is an active error. */
        <div data-testid="location-object-header" className="object-title-content">
          {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {/* Left: two-line identity */}
            <div className="v2-detail-identity">
              {loading && !location && <div style={S.emptyState}>Loading…</div>}
              {!loading && !error && !location && (
                <div style={S.emptyState}>Location not found.</div>
              )}
              {location && (
                <>
                  {/* Line 1: market · alias · M&A */}
                  <div data-testid="location-identity-bar" className="v2-header-line v2-header-title" title={[location.market, location.locationAlias, location.mAndA, headerAreaLabel].filter(Boolean).join(" · ")}>
                    <span style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, color: T.dark, letterSpacing: -0.3 }}>
                      {location.market}
                    </span>
                    {location.locationAlias && (
                      <>
                        <span style={{ fontSize: T.fsMd, fontWeight: T.fwRegular, color: T.borderDark, margin: "0 7px" }}>·</span>
                        <span style={{ fontSize: T.fsMd, color: T.light, fontWeight: T.fwRegular }}>{location.locationAlias}</span>
                      </>
                    )}
                    {location.mAndA && (
                      <>
                        <span style={{ fontSize: T.fsMd, fontWeight: T.fwRegular, color: T.borderDark, margin: "0 7px" }}>·</span>
                        <span style={{ fontSize: T.fsMd, color: T.light, fontWeight: T.fwRegular }}>{getMAndATagName(location.mAndA)}</span>
                      </>
                    )}
                    {headerAreaLabel && (
                      <>
                        <span style={{ fontSize: T.fsMd, fontWeight: T.fwRegular, color: T.borderDark, margin: "0 7px",
                          display: headerAreaLabel?.includes(" expired)") ? "none" : undefined }}>·</span>
                        <HeaderAreaLabel testId="office-detail-area-title">{headerAreaLabel}</HeaderAreaLabel>
                      </>
                    )}
                  </div>
                  {/* Line 2: ID · Hub chip · StageBadge */}
                  <div
                    data-testid="location-header-identity-strip"
                    className="object-title-meta"
                    style={{ fontSize: T.fsXs, color: T.mid, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: T.fwSemibold }}>{location.id}</span>
                    {location.isHub && (
                      <>
                        <span style={{ color: T.light }}>·</span>
                        <span style={{ color: T.dark, fontWeight: T.fwSemibold }}>◆ Hub</span>
                      </>
                    )}
                    <span style={{ color: T.light }}>·</span>
                    <StageBadge
                      stage={location.stage}
                      canWrite={CAN_WRITE && !editMode}
                      onChange={CAN_WRITE && !editMode ? handleStageChange : undefined}
                    />
                  </div>
                </>
              )}
            </div>
            {/* Right: Ask AI + user chip (v2Shell only) */}
            {headerRightSlot && (
              <div className="v2-detail-controls" style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <RecordActionsHost />
                {headerSearch}
                {headerRightSlot}
              </div>
            )}
          </div>
        </div>
      ) : showParentTrail ? (
        /* ── LOCATION-DETAIL-HEADER-V2: object header band ────────
           Renders when showParentTrail=true but NOT in v2Shell mode. */
        <div
          data-testid="location-object-header"
          style={{ padding: "14px 18px" }}
        >
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {/* Left: market · alias */}
            <div>
              {loading && !location && <div style={S.emptyState}>Loading…</div>}
              {!loading && !error && !location && (
                <div style={S.emptyState}>Location not found.</div>
              )}
              {location && (
                <>
                  <span data-testid="location-identity-bar">
                    <span style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, color: T.dark, letterSpacing: -0.3 }}>
                      {location.market}
                    </span>
                    {location.locationAlias && (
                      <span style={{ fontSize: T.fsMd, color: T.mid, fontWeight: T.fwSemibold }}>
                        {" · "}{location.locationAlias}
                      </span>
                    )}
                    {headerAreaLabel && <>
                      <span style={{ color: T.borderDark, margin: "0 7px" }}>·</span>
                      <HeaderAreaLabel testId="office-detail-area-title">{headerAreaLabel}</HeaderAreaLabel>
                    </>}
                  </span>
                  <div
                    data-testid="location-header-identity-strip"
                    style={{ fontSize: T.fsXs, color: T.mid, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: T.fwSemibold }}>{location.id}</span>
                    {STAGES[location.stage]?.label && (
                      <>
                        <span style={{ color: T.light }}>·</span>
                        <StageBadge stage={location.stage} canWrite={false} />
                      </>
                    )}
                    {location.isHub && (
                      <>
                        <span style={{ color: T.light }}>·</span>
                        <span style={{ color: T.dark, fontWeight: T.fwSemibold }}>◆ Hub</span>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            {/* Right: restyled prev/next pager */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              {onPrev && prevLabel && (
                <button
                  onClick={onPrev}
                  className="wos-btn"
                  data-testid="location-prev-button"
                  title={`Previous: ${prevLabel}`}
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    borderRadius: 9,
                    cursor: "pointer",
                    padding: "7px 13px",
                    maxWidth: 180,
                    color: T.dark,
                    fontSize: T.fsSm5,
                    fontWeight: T.fwSemibold,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span style={{ color: T.mid }}>‹</span> {prevLabel}
                </button>
              )}
              {onNext && nextLabel && (
                <button
                  onClick={onNext}
                  className="wos-btn"
                  data-testid="location-next-button"
                  title={`Next: ${nextLabel}`}
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    borderRadius: 9,
                    cursor: "pointer",
                    padding: "7px 13px",
                    maxWidth: 180,
                    color: T.dark,
                    fontSize: T.fsSm5,
                    fontWeight: T.fwSemibold,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {nextLabel} <span style={{ color: T.mid }}>›</span>
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── Legacy (non-v2) title band — unchanged ─────────────── */
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 28px 12px" }}>
        {/* LOCATION-PAGER-VERTICAL-CENTER — ErrorBanner lifted above the
            outer flex row so it does not disturb the centering geometry. */}
        <ErrorBanner error={error} onDismiss={() => setError(null)} />

        {/* Outer row: left column (breadcrumb + title stacked) sits on the
            left; the prev/next pager sits on the right and is vertically
            centered against the full height of the left column via
            alignItems:"center" on this wrapper. Previously the pager
            shared a row only with the breadcrumb, so it top-aligned to
            the first line instead of floating at the midpoint of the
            two-line block. Moving the identity bar inside the left column
            gives the pager its correct visual anchor. */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* Left column: breadcrumb on top, identity bar (title) below. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <button
              onClick={onBack}
              className="wos-btn"
              data-testid="location-back-button"
              style={{
                background: "none", border: "none", cursor: "pointer", padding: 0,
                display: "inline-flex", alignItems: "center", gap: 5,
                color: T.mid, fontSize: T.fsSm, fontWeight: T.fwSemibold,
              }}
            >
              {/* LP1.4 — chevron `‹` (U+2039) replaces long-tail arrow `←`
                  (U+2190) across all three nav surfaces (back / prev / next). */}
              ‹ {backLabel}
            </button>

            {/* LP1.1 — only render the Loading… empty state when there is no
                previously-loaded location to display. During navigation
                between locations the prior `location` stays in state (the
                fetch effect at line ~230 sets `setLoading(true)` but does
                NOT clear `location`), so we let the stale content remain
                visible and swap in cleanly when the new fetch resolves.
                Without this guard, Loading… renders ABOVE the existing
                content during the fetch interim and pushes the title down,
                producing the "drops by an inch" jump on Next-button clicks. */}
            {loading && !location && <div style={S.emptyState}>Loading…</div>}

            {!loading && !error && !location && (
              <div style={S.emptyState}>Location not found.</div>
            )}

            {location && (
              <>
                {/* LOC-HEADER-MA-VALUE — middle-dot separators between {market},
                    {alias}, and {mAndA}. Each segment after the city shares the
                    subtitle style (14px / 500 / T.mid). M&A segment only renders
                    when location.mAndA is truthy. */}
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: 8 }} data-testid="location-identity-bar">
                  <span style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, color: T.dark, letterSpacing: -0.3 }}>
                    {location.market}
                    {location.locationAlias && (
                      <span style={{ fontSize: T.fsMd, color: T.mid, fontWeight: T.fwSemibold }}>
                        {" · "}{location.locationAlias}
                      </span>
                    )}
                    {location.mAndA && (
                      <span style={{ fontSize: T.fsMd, color: T.mid, fontWeight: T.fwSemibold }}>
                        {" · "}{getMAndATagName(location.mAndA)}
                      </span>
                    )}
                  </span>
                  {headerAreaLabel && <>
                    <span style={{ color: T.borderDark, margin: "0 7px" }}>·</span>
                    <HeaderAreaLabel testId="office-detail-area-title">{headerAreaLabel}</HeaderAreaLabel>
                  </>}
                </div>
                {/* OFFICE-OVERVIEW-MOVE-STAGE-TO-HEADER: ID · Hub · StageBadge
                    row below the market/alias title (legacy non-v2Shell path). */}
                <div
                  data-testid="location-header-identity-strip"
                  style={{ fontSize: T.fsXs, color: T.mid, display: "flex", alignItems: "center", gap: 4 }}
                >
                  <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: T.fwSemibold }}>{location.id}</span>
                  {location.isHub && (
                    <>
                      <span style={{ color: T.light }}>·</span>
                      <span style={{ color: T.dark, fontWeight: T.fwSemibold }}>◆ Hub</span>
                    </>
                  )}
                  <span style={{ color: T.light }}>·</span>
                  <StageBadge
                    stage={location.stage}
                    canWrite={CAN_WRITE && !editMode}
                    onChange={CAN_WRITE && !editMode ? handleStageChange : undefined}
                  />
                </div>
              </>
            )}
          </div>

          {/* Pager — right-side child of the outer row. alignItems:"center"
              on the outer row vertically centers this against the full
              height of the left column (breadcrumb + title). Prev + Next
              are outlined buttons with IDENTICAL fixed widths (150px —
              midpoint per brief; ellipsis handles long market names). Each
              is hidden independently when its callback isn't provided
              (first ranked location → no Prev, last ranked → no Next). */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {onPrev && prevLabel && (
              <button
                onClick={onPrev}
                className="wos-btn"
                data-testid="location-prev-button"
                title={`Previous: ${prevLabel}`}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  padding: "4px 8px",
                  width: 150,
                  color: T.mid, fontSize: T.fsSm, fontWeight: T.fwSemibold,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                ‹ {prevLabel}
              </button>
            )}
            {onNext && nextLabel && (
              <button
                onClick={onNext}
                className="wos-btn"
                data-testid="location-next-button"
                title={`Next: ${nextLabel}`}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  padding: "4px 8px",
                  width: 150,
                  color: T.mid, fontSize: T.fsSm, fontWeight: T.fwSemibold,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {nextLabel} ›
              </button>
            )}
          </div>
        </div>
        </div>
      )}
      </div>
      {/* /Zone 1 */}

      {location && (
        <>
      <div data-testid="location-zone-body" className={v2Shell ? "object-detail-body" : undefined} style={v2Shell ? { paddingBottom: 64 } : undefined}>
      {/* ── Zone 2: Unified bar ────────────────────────────────────── */}
      <DetailNavBand
        enabled={false}
        data-testid="location-zone-tabs"
        style={v2Shell ? undefined : { background: T.card, borderTop: `1px solid ${T.tintBorder}`, borderBottom: `1px solid ${T.tintBorder}`, flexShrink: 0 }}
      >
          {/*
            Slice L1 — restored 4-tab parity with the legacy app.
            NOTE on the "building" key: the user-facing label is
            "Location" but the internal state key stays "building" to
            avoid a state-migration ripple — `initialTab` callers
            (e.g. the Lease Countdown report drilldown) still pass
            "building" and "lease". Keep this mismatch intentional;
            do not "fix" it.
            LOCATION-HEADER-COLLAPSE-S1: borderTop removed (Zone 1's
            borderBottom already separates the two rows); height fixed
            at 41px; back-link absolutely positioned at far-left so it
            sits outside the centered bar-content flow.
          */}
          {/* LOCATION-HEADER-S1: unified bar for v2Shell — back link | tabs | pager | edit.
              Non-v2Shell path keeps the original tab-strip-only layout.
              LOCATION-HEADER-FIX-S1: pager now lives in this bar, left of the edit divider.
              HEADCOUNT-TREND-HEADER-CHROME: v2Shell path uses shared DetailUnifiedBar so
              HeadcountTrendReport can reuse the same layout chrome without duplication. */}
          {v2Shell ? (
            <DetailUnifiedBar
              oneRow
              recordNavigation
              testId="location-unified-bar"
              barContentTestId="location-bar-content"
              backLabel={backLabel}
              onBack={onBack}
              backTestId="location-back-button"
              tabs={visibleTabs.length > 1 ? visibleTabs.map((t) => ({ key: t.key, label: t.text })) : []}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              tabBadge={undefined}
              rightSlot={
                <>
                  {/* LocationNavPair — uses prevLabel/nextLabel/onPrev/onNext from
                      props. AppShell provides display names (locationAlias ?? market)
                      and navigate callbacks for both the orderedIds route-state path
                      (OfficesPage filtered-list entry) and the props-driven path.
                      LocationDetail does not duplicate that logic here. */}
                  {/* LSE-EDIT-LOCATION-MODAL-PHASE-1 — Edit button shown only on
                      the Location tab. LOCATION-HEADER-FIX-S1: divider uses auto-margin
                      only when the pager is absent; pager already owns the auto-margin. */}
                  {CAN_WRITE && !editMode && activeTab === "building" && (
                    <>
                      <div
                        data-testid="location-unified-bar-divider-left"
                        style={{ width: 1, alignSelf: "stretch", background: T.planCompBorder, margin: "6px 4px" }}
                      />
                      <button
                        type="button"
                        className="wos-btn"
                        onClick={handleEditOpen}
                        data-chrome-action="edit"
                        data-testid="location-edit-button"
                        style={{
                          marginLeft: 0,
                          background: "transparent",
                          border: "none",
                          color: T.blue,
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: T.fsXs,
                          fontWeight: T.fwSemibold,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        Edit office
                      </button>
                    </>
                  )}
                  {/* EXPERIENCE-TAB-FIX — "Edit amenities" right-anchored on Experience tab. */}
                  {CAN_WRITE && activeTab === "people" && (
                    <>
                      <div
                        style={{ width: 1, alignSelf: "stretch", background: T.planCompBorder, margin: "6px 4px" }}
                      />
                      <button
                        type="button"
                        className="wos-btn"
                        data-chrome-action="edit"
                        data-testid="amenity-edit-btn"
                        onClick={() => setAmenityEditOpen((o) => !o)}
                        style={{
                          marginLeft: 0,
                          background: "transparent",
                          border: "none",
                          color: T.blue,
                          padding: "6px 8px",
                          borderRadius: 6,
                          fontSize: T.fsXs,
                          fontWeight: T.fwSemibold,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        {amenityEditOpen ? "Cancel" : "Edit amenities"}
                      </button>
                    </>
                  )}
                  <LocationNavPair
                    prevLabel={prevLabel}
                    nextLabel={nextLabel}
                    onPrev={onPrev}
                    onNext={onNext}
                  />
                </>
              }
            />
          ) : (
            /* Non-v2Shell: plain tab strip only — no back link, no pager. */
            <div data-testid="location-unified-bar" style={{ display: "flex", alignItems: "center", gap: 2, position: "relative", height: 41, containerType: "inline-size" }}>
              <div data-testid="location-bar-content" style={{ width: "100%", maxWidth: 1400, margin: "0 auto", display: "flex", alignItems: "stretch", paddingLeft: 0 }}>
                {/* O-36: hide tab strip when only one tab is visible */}
                {visibleTabs.length > 1 && visibleTabs.map((tab) => {
                  const active = activeTab === tab.key;
                  const badge  = undefined;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      data-testid={`tab-button-${tab.key}`}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "8px 10px",
                        fontSize: T.fsSm5,
                        fontWeight: T.fwSemibold,
                        color: active ? T.blue : T.mid,
                        cursor: "pointer",
                        borderBottom: `2px solid ${active ? T.blue : "transparent"}`,
                        transition: "all 0.2s",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      {tab.text}
                      {badge > 0 && (
                        <span
                          data-testid={`tab-badge-${tab.key}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: 16,
                            height: 16,
                            borderRadius: 8,
                            background: T.red,
                            color: T.white,
                            fontSize: T.fsXxs,
                            fontWeight: T.fwBold,
                            padding: "0 4px",
                            lineHeight: 1,
                          }}
                        >
                          {badge}
                        </span>
                      )}
                    </button>
                  );
                })}
                {CAN_WRITE && !editMode && activeTab === "building" && (
                  <button
                    type="button"
                    className="wos-btn"
                    onClick={handleEditOpen}
                    data-testid="location-edit-button"
                    style={{
                      marginLeft: "auto",
                      background: "transparent",
                      border: "none",
                      color: T.blue,
                      padding: "6px 8px",
                      borderRadius: 6,
                      fontSize: T.fsXs,
                      fontWeight: T.fwSemibold,
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    Edit office
                  </button>
                )}
                {CAN_WRITE && activeTab === "people" && (
                  <button
                    type="button"
                    className="wos-btn"
                    data-testid="amenity-edit-btn"
                    onClick={() => setAmenityEditOpen((o) => !o)}
                    style={{
                      marginLeft: "auto",
                      background: "transparent",
                      border: "none",
                      color: T.blue,
                      padding: "6px 8px",
                      borderRadius: 6,
                      fontSize: T.fsXs,
                      fontWeight: T.fwSemibold,
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    {amenityEditOpen ? "Cancel" : "Edit amenities"}
                  </button>
                )}
              </div>
            </div>
          )}
      </DetailNavBand>
      {/* /Zone 2 */}


      {/* ── Zone 3: Body content (page T.bg shows through) ───────── */}
      {/* LOCATION-HEADER-NAV-ALIGN-S7: the back-link only shares the row with the tabs,
          so its clearance belongs solely on location-bar-content. This container keeps
          maxWidth:1400 + margin:0 auto + 32px horizontal padding matching PageHeaderStack
          (the same constraint the Offices list uses), so the right edge and content width
          align within a few px across list and detail views. Its gutter now grows/shrinks
          with the viewport exactly like the Offices list, independent of the tab bar's
          back-link clearance. */}
      {/* Match lease detail: only the body scrolls; identity, tabs and their
          overlays remain outside it. Keep legacy callers shell-scrolled. */}
      <div className={`${v2Shell ? "object-detail-entry" : ""} ${activeTab === "building" ? "office-overview-content" : ""}`.trim()} style={{ maxWidth: 1400, margin: "0 auto", paddingInline: 32, paddingBottom: 40, paddingTop: v2Shell ? undefined : S.paneGap }}>
          {/* Slice L4 Gate 2 — Tab content dispatch.
              - "building" (label "Location"): Photo + Details two-column,
                no outer S.card wrapper because PhotoPanel and
                LocationDetailsPanel each carry their own chrome (they
                were standalone cards in the L2/L3 always-visible slot).
                The `location-header-cols` testid moves with the grid so
                existing L2 assertions still resolve when this tab is
                active.
              - "cost": single panel inside the standard
                S.card wrapper (legacy behavior preserved).
              - "utilization": UtilizationPanel in its own S.card.
                HeadcountPanel was removed from this tab (UTIL-TAB-HEADCOUNT-REMOVE);
                headcount belongs on the People tab and Overview synthesis
                strip only. UtilizationPanel self-styles with `S.panelPad`,
                so the outer S.card adds the same chrome it had before. */}
          {activeTab === "building" && (
            // OFFICE-OVERVIEW-REMOVE-SCORE: retained 2×2 overview grid.
            //   Top-left:    PhotoPanel           (showHealth=false, locationId prop)
            //   Top-right:   LocationOverviewCard  (renamed "Capacity & occupancy")
            //   Bottom-left: LeaseAndCostPane       (O-36 — "Lease & cost")
            //   Bottom-right: SpaceFitOverviewPane (new — replaces score card)
            //
            // testids preserved for existing test suite:
            //   location-header-card, location-header-cols, photo-frame, space-overview-card.
            <>
              {/* W4 — CLOSURE-WF-REACTIVE-LAUNCH: post-vacate offer (editor/admin only). */}
              {CAN_WRITE && workflowsNavEnabled && showClosureOffer && !closureOfferDismissed && (
                <ClosureOfferPanel
                  locationAlias={location.locationAlias || location.id}
                  onAccept={handleClosureOfferAccept}
                  onDismiss={() => setClosureOfferDismissed(true)}
                />
              )}
              {/* Compact persistent chip after the full panel is dismissed. */}
              {CAN_WRITE && workflowsNavEnabled && showClosureOffer && closureOfferDismissed && (
                <ClosureOfferChip onAccept={handleClosureOfferAccept} />
              )}
              <div
                data-testid="location-header-card"
              >
                <style>{`
                  @media (max-width: 900px) {
                    [data-testid="location-header-cols"] { grid-template-columns: minmax(0, 1fr) !important; }
                    [data-testid="location-header-cols"] > div { min-width: 0; }
                  }
                  @media (max-width: 600px) {
                    .office-overview-content { padding-left: 8px !important; padding-right: 8px !important; }
                    .office-overview-space-fit-scroll { overflow-x: auto; }
                    .office-overview-space-fit-canvas { min-width: 520px; }
                  }
                `}</style>
                {/* Top-row shells align; lower panes retain their natural height. */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: canSeeLeaseCostPane
                      ? "minmax(0, 1fr) minmax(0, 1fr)"
                      : "repeat(3, minmax(0, 1fr))",
                    gridTemplateRows: "auto auto",
                    gap: S.paneGap,
                    alignItems: "stretch",
                  }}
                  data-testid="location-header-cols"
                >
                  {/* [0,0] Top-left: Photo card. showHealth=false removes HealthBadge
                      from the card header (OVERVIEW-ALIGN). locationId renders below
                      the truncated address. Stage pill is kept.
                      The shell stretches while the image frame stays fixed. */}
                  <Card style={{ padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.cardShadow, height: "100%" }}>
                    <PhotoPanel
                      location={location}
                      isAdmin={IS_ADMIN}
                      editMode={editMode}
                      onChanged={refetch}
                      address={location.address ?? null}
                      health={location.health}
                      showHealth={false}
                      locationId={location.id}
                      premises={location.suite ?? null}
                      canWrite={CAN_WRITE && !editMode}
                      onHealthChange={CAN_WRITE && !editMode ? handleHealthChange : undefined}
                    />
                  </Card>

                  {/* [0,1] Top-right: Capacity & occupancy card (OFFICE-OVERVIEW-REMOVE-SCORE).
                      Sharing ratio reads latestHeadcount.total directly (staleness gate removed).
                      Attendance bar shows avg/peak from most-recent utilization entry.
                      Self-fetches opex for Cost per seat tile. */}
                  <Card style={{ padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.cardShadow, height: "100%" }}>
                    <LocationOverviewCard
                      location={location}
                      latestHeadcount={location.latestHeadcount ?? null}
                      locationId={location.id}
                      badgeIntegrationPending={!!location.badgeIntegrationPending}
                    />
                  </Card>

                  {/* [1,0] Bottom-left: Lease & cost pane (O-36 — replaces What's ahead).
                      Self-fetches listLocationLeases + opex; passes activeLease for
                      key-date derivation when no active leases are returned yet. */}
                  {canSeeLeaseCostPane && <Card style={{ padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.cardShadow, alignSelf: "start" }}>
                    <LeaseAndCostPane
                      location={location}
                      activeLease={primaryLease ?? getActiveLease(location) ?? null}
                      projectDecisions={projectDecisions}
                      aheadPortalTarget={aheadSlotEl}
                    />
                  </Card>}

                  {/* [1,1] Bottom-right: Space Fit pane (OFFICE-OVERVIEW-REMOVE-SCORE).
                      New standalone pane — replaces the removed Location Score card.
                      Uses computeSpaceFit + canonical RAG tokens from spaceFitVerdict.js. */}
                  <Card className="office-overview-space-fit-scroll" style={{ padding: 0, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.cardShadow, alignSelf: "start" }}>
                    <div className="office-overview-space-fit-canvas">
                    <SpaceFitOverviewPane
                      location={location}
                      latestHeadcount={location.latestHeadcount ?? null}
                      badgeIntegrationPending={!!location.badgeIntegrationPending}
                    />
                    </div>
                  </Card>
                </div>
                {/* Task #8581 row 3: What's ahead (portal slot, gated with the
                    lease & cost pane) beside compact Projects & activity
                    (gated by the Projects nav). Stacks below 900px. */}
                {(canSeeLeaseCostPane || projectsNavEnabled) && (
                  <div
                    data-testid="location-overview-row3"
                    className="location-overview-row3"
                    style={{
                      display: "grid",
                      gridTemplateColumns: canSeeLeaseCostPane && projectsNavEnabled
                        ? "minmax(0, 1fr) minmax(0, 1fr)"
                        : "minmax(0, 1fr)",
                      gap: S.paneGap,
                      alignItems: "start",
                      marginTop: S.paneGap,
                    }}
                  >
                    <style>{`@media (max-width: 900px) { .location-overview-row3 { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
                    {canSeeLeaseCostPane && (
                      <div ref={setAheadSlotEl} data-testid="whats-ahead-slot" style={{ minWidth: 0 }} />
                    )}
                    {projectsNavEnabled && (
                      <OfficeProjectsActivityPane
                        locationId={location.id}
                        onViewAll={() => navigate(buildProjectsUrl(location.id))}
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {/* Cost tab retired (O-36) — CostTab has been removed; cost data now lives
              in LeaseAndCostPane within the Overview tab. */}
          {/* Experience tab (formerly People) — keep-alive: mounted only when the
              tab is visible; hidden via display:none when another tab is active.
              Internal key stays "people" to avoid URL/session breakage.
              EXPERIENCE-TAB-FIX: location passed for seat-avail KPI; amenityEditOpen
              is the controlled edit-panel state driven by the tab-strip button. */}
          {visibleTabs.some((t) => t.key === "people") && (
          <div style={{ display: activeTab === "people" ? undefined : "none" }}>
            <ExperienceTab
              locationId={location.id}
              canWrite={CAN_WRITE}
              location={location}
              editOpen={amenityEditOpen}
              onEditOpenChange={setAmenityEditOpen}
            />
          </div>
          )}
          {activeTab === "service" && (
            <ServiceTab />
          )}
          {activeTab === "intel" && (
            <IntelTab
              locationId={location.id}
              locationText={location.name || location.id}
              currentRole={currentRole}
            />
          )}
          {activeTab === "utilization" && (
            <UtilizationTab location={location} />
          )}


          {/* LSE-EDIT-LOCATION-MODAL-PHASE-1 — Edit location data modal.
              disableBackdropClose=true prevents accidental dismiss.
              Dirty-form guard inside EditLocationModal shows a Discard
              confirm when Cancel/ESC fires with unsaved changes.
              The 5 unsurfaced editable fields (currency, district,
              floors, lat, lng) are tracked in tasks/todo.md as
              Phase-2 backlog. */}
          {CAN_WRITE && location && (
            <EditLocationModal
              isOpen={editMode}
              canArchive={canArchive && currentRole === "admin"}
              onArchived={handleOfficeArchived}
              onArchiveActiveChange={(value) => { archiveActiveRef.current = value; }}
              onClose={handleEditCancel}
              onSave={handleEditSave}
              isDirty={isEditDirty}
              suppressDismiss={!!pendingNav}
              location={location}
              editDraft={editDraft}
              editErrors={editErrors}
              editFormError={editFormError}
              editSubmitting={editSubmitting}
              editNoChanges={editNoChanges}
              onDraftChange={handleEditDraftChange}
              editPhotoPending={editPhotoPending}
              onEditPhotoChange={handleEditPhotoChange}
              capacitySchedule={mergedCapacitySchedule}
              onCapacityScheduleAdd={handleCapacityScheduleAdd}
              onCapacityScheduleRemove={handleCapacityScheduleRemove}
              capacityScheduleAddError={capacityScheduleAddError}
              capacityScheduleRemoving={capacityScheduleRemoving}
              onCapacityScheduleEdit={handleCapacityScheduleEdit}
              capacityScheduleEditing={capacityScheduleEditing}
              onCapacityScheduleEditingChange={setCapacityScheduleEditing}
              capacityScheduleEditError={capacityScheduleEditError}
              capacityScheduleRemoveError={capacityScheduleRemoveError}
              headcountSchedule={mergedHeadcountSchedule}
              onHeadcountScheduleAdd={handleHeadcountScheduleAdd}
              onHeadcountScheduleRemove={handleHeadcountScheduleRemove}
              headcountScheduleAddError={headcountScheduleAddError}
              headcountScheduleRemoving={headcountScheduleRemoving}
              onHeadcountScheduleEdit={handleHeadcountScheduleEdit}
              headcountScheduleEditing={headcountScheduleEditing}
              onHeadcountScheduleEditingChange={setHeadcountScheduleEditing}
              headcountScheduleEditError={headcountScheduleEditError}
              headcountScheduleRemoveError={headcountScheduleRemoveError}
            />
          )}

      </div>
      </div>
      {/* /Zone 3 */}
        </>
      )}
      {/* LP1.6 — Unsaved-changes nav guard. Renders only when App.jsx
          has called onBeforeNavigate while editMode is true. The modal
          owns no nav state of its own; the three handlers each resolve
          the pending promise so App.jsx can fire (or skip) the actual
          location change. Mounted at the LocationDetail root so the
          backdrop covers the entire detail surface, including the
          tab strip where the Save/Cancel cluster lives. */}
      {pendingNav && location && (
        <UnsavedChangesModal
          currentLocationName={location.market}
          targetLabel={pendingNav.targetLabel || "another location"}
          saving={modalSaving}
          saveError={modalSaveError}
          onCancel={handleUnsavedCancel}
          onDiscard={handleUnsavedDiscard}
          onSave={handleUnsavedSave}
        />
      )}
      {/* W4 — CLOSURE-WF-REACTIVE-LAUNCH: drawer opened after the dedup check
          confirms no existing open run for this source office. Editor/admin only —
          the CAN_WRITE gate also wraps the offer panel that leads here. */}
      {CAN_WRITE && workflowsNavEnabled && closureDrawerOpen && (
        <ClosureStartDrawer
          canEdit={CAN_WRITE}
          onClose={() => setClosureDrawerOpen(false)}
          onLaunched={(runId) => {
            setClosureDrawerOpen(false);
            setShowClosureOffer(false);
            if (onOpenWorkflowRun) {
              onOpenWorkflowRun(runId);
            }
          }}
          onDedupNavigate={(existingId) => {
            setClosureDrawerOpen(false);
            setShowClosureOffer(false);
            if (onOpenWorkflowRun) {
              onOpenWorkflowRun(existingId);
            } else {
              window.location.assign(`/workflows/runs/${existingId}`);
            }
          }}
          initialSourceLocationId={closurePreFill.sourceLocationId}
          initialCloseDate={closurePreFill.closeDate}
          initialOwnerName={closurePreFill.ownerName}
          launchedFrom={closurePreFill.launchedFrom}
        />
      )}
    </RecordLayout>
  );
});
